import React, { useEffect, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useLanguage } from "@/context/LanguageContext";
import {
  Mic, Camera, Eye, RotateCw, AudioLines, HeartPulse,
  Bluetooth, Watch, Thermometer, Smile, ScanFace, Power,
} from "lucide-react";

const CHANNEL_ICON = {
  voice: Mic,
  face: ScanFace,
  gaze: Eye,
  head_orientation: RotateCw,
  speech_dynamics: AudioLines,
  emotional_tone: Smile,
  camera: Camera,
  environment: Thermometer,
  capsule_sensors: HeartPulse,
  device_proximity: Bluetooth,
  wearable_identity: Watch,
};

const CHANNEL_LABEL = {
  voice: { tr: "Ses", en: "Voice" },
  face: { tr: "Yüz", en: "Face" },
  gaze: { tr: "Göz bakışı", en: "Gaze" },
  head_orientation: { tr: "Baş yönelimi", en: "Head orientation" },
  speech_dynamics: { tr: "Konuşma ritmi", en: "Speech dynamics" },
  emotional_tone: { tr: "Duygusal ton", en: "Emotional tone" },
  camera: { tr: "Kamera", en: "Camera" },
  environment: { tr: "Ortam", en: "Environment" },
  capsule_sensors: { tr: "Kapsül sensörleri", en: "Capsule sensors" },
  device_proximity: { tr: "Cihaz yakınlığı", en: "Device proximity" },
  wearable_identity: { tr: "Giyilebilir kimliği", en: "Wearable identity" },
};

export default function ConsciousPage() {
  const { t, language } = useLanguage();
  const [channels, setChannels] = useState([]);
  const [settings, setSettings] = useState(null);
  const [prefs, setPrefs] = useState(null);
  const [error, setError] = useState("");

  async function load() {
    try {
      const [cs, st, pr] = await Promise.all([
        api.get("/conscious/channels"),
        api.get("/conscious/settings"),
        api.get("/conscious/preferences"),
      ]);
      setChannels(cs.data);
      setSettings(st.data);
      setPrefs(pr.data);
    } catch (e) {
      setError(formatApiErrorDetail(e.response?.data?.detail) || e.message);
    }
  }

  useEffect(() => { load(); }, []);

  async function toggleChannel(name, value) {
    const { data } = await api.patch("/conscious/privacy", { channels: { [name]: value } });
    setSettings((s) => ({ ...s, channels: data.channels }));
  }

  async function toggleFlag(flag, value) {
    const { data } = await api.patch("/conscious/flag", { flag, value });
    setSettings(data);
  }

  async function patchPref(key, value) {
    const { data } = await api.patch("/conscious/preferences", { preferences: { [key]: value } });
    setPrefs(data);
  }

  return (
    <div className="space-y-8" data-testid="conscious-page">
      <header>
        <div className="text-[11px] font-semibold uppercase tracking-[0.16em] text-slate-500">
          ME Conscious Interaction Engine
        </div>
        <h1 className="mt-1 text-3xl font-semibold tracking-tight text-slate-900">
          {language === "tr" ? "Bilinçli Etkileşim" : "Conscious Interaction"}
        </h1>
        <p className="mt-1 max-w-2xl text-sm text-slate-500">
          {language === "tr"
            ? "ME sürekli bir varlıktır; ne zaman algılayacağına izin veren tek kişi sensin. Her kanal varsayılan olarak kapalıdır; yalnızca açıkça onayladığın kanallarda dinler."
            : "ME is a continuous being; you alone authorise when it may perceive. Every channel is off by default and ME only listens through the channels you explicitly approve."}
        </p>
      </header>

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

      {settings && (
        <section className="mios-card" data-testid="conscious-flags">
          <div className="mios-card-body grid gap-3 md:grid-cols-2">
            <ToggleRow
              icon={Power}
              label={language === "tr" ? "Uyandırma kelimesi 'ME'" : "Wake word 'ME'"}
              value={settings.wake_word_active}
              onChange={(v) => toggleFlag("wake_word_active", v)}
              testid="flag-wake-word"
            />
            <ToggleRow
              icon={Mic}
              label={language === "tr" ? "Sürekli öğrenme (tercihler — tıbbi olgular dahil değil)" : "Continuous learning (preferences only — never medical facts)"}
              value={settings.learning_enabled}
              onChange={(v) => toggleFlag("learning_enabled", v)}
              testid="flag-learning"
            />
          </div>
        </section>
      )}

      <section className="mios-card" data-testid="channels-card">
        <div className="mios-card-header">
          <div className="mios-card-title">
            {language === "tr" ? "Algı Kanalları" : "Perception Channels"}
          </div>
          <div className="mios-card-sub">{channels.length}</div>
        </div>
        <div className="mios-card-body grid gap-3 md:grid-cols-2 lg:grid-cols-3">
          {channels.map((c) => {
            const Icon = CHANNEL_ICON[c.name] || Mic;
            const label = (CHANNEL_LABEL[c.name] && CHANNEL_LABEL[c.name][language]) || c.name;
            const enabled = settings?.channels?.[c.name];
            return (
              <div key={c.name} className={`rounded-lg border border-slate-200 bg-white p-4 ${enabled ? "ring-2 ring-[color:var(--mios-brand)]" : ""}`} data-testid={`channel-card-${c.name}`}>
                <div className="flex items-start justify-between gap-3">
                  <div className="flex items-center gap-2.5">
                    <Icon className="h-4 w-4 text-slate-600" />
                    <div>
                      <div className="text-sm font-medium text-slate-900">{label}</div>
                      <div className="mono text-[11px] text-slate-500">{c.name}</div>
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={() => toggleChannel(c.name, !enabled)}
                    data-testid={`channel-toggle-${c.name}`}
                    className={`mios-pill ${enabled ? "mios-pill-normal" : "mios-pill-warning"} cursor-pointer`}
                  >
                    {enabled ? (language === "tr" ? "Açık" : "On") : (language === "tr" ? "Kapalı" : "Off")}
                  </button>
                </div>
                <div className="mt-2 text-xs text-slate-600">{c.description}</div>
              </div>
            );
          })}
        </div>
      </section>

      {prefs && (
        <section className="mios-card" data-testid="preferences-card">
          <div className="mios-card-header">
            <div className="mios-card-title">
              {language === "tr" ? "Etkileşim Tercihleri" : "Interaction Preferences"}
            </div>
            <div className="mios-card-sub">
              {language === "tr" ? "Yalnızca tarz — tıbbi içerik değil" : "Style only — never medical content"}
            </div>
          </div>
          <div className="mios-card-body grid gap-4 md:grid-cols-3">
            <PrefSelect
              label={language === "tr" ? "Karşılama" : "Greeting"}
              testid="pref-greeting"
              value={prefs.preferences?.greeting}
              options={["Merhaba", "Selam ME", "Günaydın", "İyi akşamlar"]}
              onChange={(v) => patchPref("greeting", v)}
            />
            <PrefSelect
              label={language === "tr" ? "Konuşma hızı" : "Speaking speed"}
              testid="pref-speed"
              value={prefs.preferences?.speaking_speed}
              options={["slow", "natural", "fast"]}
              onChange={(v) => patchPref("speaking_speed", v)}
            />
            <PrefSelect
              label={language === "tr" ? "Terminoloji düzeyi" : "Terminology level"}
              testid="pref-terminology"
              value={prefs.preferences?.terminology_level}
              options={["plain", "balanced", "clinical"]}
              onChange={(v) => patchPref("terminology_level", v)}
            />
          </div>
        </section>
      )}
    </div>
  );
}

function ToggleRow({ icon: Icon, label, value, onChange, testid }) {
  return (
    <div className="flex items-center justify-between gap-3 rounded-lg border border-slate-200 bg-white px-3 py-3" data-testid={testid}>
      <div className="flex items-center gap-2.5">
        <Icon className="h-4 w-4 text-slate-600" />
        <div className="text-sm text-slate-900">{label}</div>
      </div>
      <button
        type="button"
        onClick={() => onChange(!value)}
        className={`mios-pill ${value ? "mios-pill-normal" : "mios-pill-warning"} cursor-pointer`}
      >
        {value ? "ON" : "OFF"}
      </button>
    </div>
  );
}

function PrefSelect({ label, value, options, onChange, testid }) {
  return (
    <div data-testid={testid}>
      <label className="mios-card-sub mb-1 block">{label}</label>
      <select className="mios-input" value={value || ""} onChange={(e) => onChange(e.target.value)}>
        {options.map((o) => (<option key={o} value={o}>{o}</option>))}
      </select>
    </div>
  );
}
